import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import logUpsellMidpointShown from '@alltrails/amplitude/events/logUpsellMidpointShown';
import useUser from '@alltrails/context/hooks/useUser';
import Typography from '@alltrails/core/components/Typography';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import CheckSm from '@alltrails/denali/icons/CheckSm';
import styles from './PeakFeatureList.module.scss';

const baseFeatures = [
  { base: true, plus: true, peak: true, text: <FormattedMessage defaultMessage="Save trails and create lists " /> },
  { base: true, plus: true, peak: true, text: <FormattedMessage defaultMessage="Navigate to stay on route" /> }
];

const newPlusFeatures = [
  { base: false, plus: true, peak: true, text: <FormattedMessage defaultMessage="Download offline maps" /> },
  { base: false, plus: true, peak: true, text: <FormattedMessage defaultMessage="Plan for on-trail conditions" /> },
  { base: false, plus: true, peak: true, text: <FormattedMessage defaultMessage="Tour photos along the trail" /> },
  { base: false, plus: true, peak: true, text: <FormattedMessage defaultMessage="Get alerts for wrong turns" /> },
  { base: false, plus: true, peak: true, text: <FormattedMessage defaultMessage="Preview trails and terrain in 3D" /> },
  { base: false, plus: true, peak: true, text: <FormattedMessage defaultMessage="Share live activities with friends" /> },
  { base: false, plus: true, peak: true, text: <FormattedMessage defaultMessage="Find trails by distance from you" /> },
  { base: false, plus: true, peak: true, text: <FormattedMessage defaultMessage="Print maps for backup" /> },
  { base: false, plus: true, peak: true, text: <FormattedMessage defaultMessage="Go ad-free" /> }
];

const byotFreePeakFeatures = [
  { base: false, plus: false, peak: true, text: <FormattedMessage defaultMessage="Explore community heatmaps" /> },
  { base: false, plus: false, peak: true, text: <FormattedMessage defaultMessage="Build your own routes in app" /> },
  { base: false, plus: false, peak: true, text: <FormattedMessage defaultMessage="Customize AllTrails routes in app" /> },
  { base: false, plus: false, peak: true, text: <FormattedMessage defaultMessage="Identify plants and more" /> }
];

type Props = {
  tierSelected: PurchaseTier;
  disableMidpointTracking?: boolean;
  upsellTrigger?: UpsellTrigger;
  className?: string;
  hasHeaderScrollMargin?: boolean;
  hidePeak?: boolean;
};

// https://www.figma.com/design/PtYSSpohQ9rL7ZWCcdsYJK/2025-Upsell-System?node-id=6260-96740&m=dev
export default function PeakFeatureListTable({
  upsellTrigger,
  tierSelected = 'peak',
  disableMidpointTracking = false,
  className,
  hasHeaderScrollMargin,
  hidePeak
}: Props) {
  const midpointRef = useRef<HTMLTableRowElement>(null);
  const user = useUser();
  const isPromoEligible = !user || user.isPromoEligible;

  useEffect(() => {
    if (disableMidpointTracking) return;

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          logUpsellMidpointShown({
            purchase_screen_type_plan: isPromoEligible ? 'free trial' : 'non trial',
            trigger: upsellTrigger
          });
        }
      });
    });

    if (midpointRef.current) {
      observer.observe(midpointRef.current);
    }

    return () => observer.disconnect();
  }, [disableMidpointTracking, isPromoEligible, midpointRef, upsellTrigger, user]);

  const plusFeatures = newPlusFeatures;
  const peakFeatures = byotFreePeakFeatures;

  const features = hidePeak ? [...baseFeatures, ...plusFeatures] : [...baseFeatures, ...plusFeatures, ...peakFeatures];

  return (
    <>
      <table
        id="feature-list"
        className={classNames(styles.table, className, {
          [styles.peakSelected]: tierSelected === 'peak',
          [styles.plusSelected]: tierSelected === 'plus',
          [styles.headerScrollMargin]: hasHeaderScrollMargin
        })}
      >
        <thead>
          <tr>
            <th>
              <FormattedMessage defaultMessage="What you get" />
            </th>
            <th>Base</th>
            <th>Plus</th>
            {!hidePeak && <th>Peak</th>}
          </tr>
        </thead>
        <tbody>
          {features.map((feature, index) => {
            return (
              <tr key={index} {...(index === 7 ? { ref: midpointRef } : {})}>
                <td>
                  <Typography variant="text100">{feature.text}</Typography>
                </td>
                <td>{feature.base && <CheckSm color="--color-text-tertiary" />}</td>
                <td>{feature.plus && <CheckSm color={tierSelected === 'plus' ? '--color-text-primary' : '--color-text-tertiary'} />}</td>
                {!hidePeak && (
                  <td>
                    <CheckSm color={tierSelected === 'peak' ? '--color-text-primary' : '--color-text-tertiary'} />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}
